/* İçerik bölümleri: program, atölyeler, sahne alanlar, dress code, akşam, etkinlik. */

import type {
  DressCode,
  Evening,
  EventMeta,
  ProgramSession,
  SessionKind,
  SiteContent,
  Speaker,
  Workshop,
} from "../../shared/types.js";
import { SESSION_KINDS, emptyI18n } from "../../shared/types.js";
import { Field, I18nField, Panel, move, removeAt, replaceAt } from "./fields.js";

const newId = (p: string) => p + Date.now().toString(36) + Math.floor(Math.random() * 1296).toString(36);

/* ------------------------------------------------------------- program */

export function SessionsEditor({
  value,
  speakers,
  onChange,
}: {
  value: ProgramSession[];
  speakers: Speaker[];
  onChange: (v: ProgramSession[]) => void;
}) {
  const add = () =>
    onChange([
      ...value,
      {
        id: newId("s"),
        start: "12:00",
        end: "13:00",
        title: emptyI18n(),
        sub: emptyI18n(),
        kind: "plenary",
        loc: emptyI18n(),
        speakerIds: [],
      },
    ]);

  return (
    <div>
      <Header title="Program" count={value.length + " oturum"} onAdd={add} addLabel="+ Oturum" />
      <p className="dim" style={{ fontSize: 14.5, marginTop: 8 }}>
        Kaydettiğinizde oturumlar saate göre otomatik sıralanır.
      </p>

      {value.map((s, i) => (
        <Panel
          key={s.id}
          title={s.start + " · " + (s.title.tr || s.title.en || "Yeni oturum")}
          onRemove={() => onChange(removeAt(value, i))}
        >
          <div className="grid2">
            <Field
              label="Başlangıç"
              value={s.start}
              mono
              onChange={(v) => onChange(replaceAt(value, i, { ...s, start: v }))}
            />
            <Field
              label="Bitiş"
              value={s.end}
              mono
              onChange={(v) => onChange(replaceAt(value, i, { ...s, end: v }))}
            />
          </div>
          <I18nField
            label="Başlık"
            value={s.title}
            onChange={(v) => onChange(replaceAt(value, i, { ...s, title: v }))}
          />
          <I18nField
            label="Alt başlık"
            value={s.sub}
            onChange={(v) => onChange(replaceAt(value, i, { ...s, sub: v }))}
          />
          <I18nField
            label="Konum"
            value={s.loc}
            onChange={(v) => onChange(replaceAt(value, i, { ...s, loc: v }))}
          />

          <label className="field">
            <span className="field__label">Tür</span>
            <select
              className="input"
              value={s.kind}
              onChange={(e) =>
                onChange(replaceAt(value, i, { ...s, kind: e.target.value as SessionKind }))
              }
            >
              {SESSION_KINDS.map((k) => (
                <option key={k} value={k} style={{ background: "#0a1233" }}>
                  {k}
                </option>
              ))}
            </select>
          </label>

          <div className="field">
            <span className="field__label">Sahne alanlar</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {speakers.map((sp) => {
                const on = s.speakerIds.includes(sp.id);
                return (
                  <button
                    key={sp.id}
                    type="button"
                    className={"btn " + (on ? "btn--gold" : "btn--ghost")}
                    onClick={() =>
                      onChange(
                        replaceAt(value, i, {
                          ...s,
                          speakerIds: on
                            ? s.speakerIds.filter((x) => x !== sp.id)
                            : [...s.speakerIds, sp.id],
                        }),
                      )
                    }
                  >
                    {sp.name}
                  </button>
                );
              })}
            </div>
          </div>
        </Panel>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------- atölyeler */

export function WorkshopsEditor({
  value,
  onChange,
}: {
  value: Workshop[];
  onChange: (v: Workshop[]) => void;
}) {
  const add = () =>
    onChange([
      ...value,
      {
        id: newId("w"),
        name: emptyI18n(),
        partner: "",
        tag: emptyI18n(),
        time: "",
        loc: emptyI18n(),
        desc: emptyI18n(),
        detail: emptyI18n(),
      },
    ]);

  return (
    <div>
      <Header title="Atölyeler" count={value.length + " atölye"} onAdd={add} addLabel="+ Atölye" />
      {value.map((w, i) => (
        <Panel
          key={w.id}
          title={w.name.tr || w.name.en || "Yeni atölye"}
          onRemove={() => onChange(removeAt(value, i))}
          onUp={i > 0 ? () => onChange(move(value, i, i - 1)) : undefined}
          onDown={i < value.length - 1 ? () => onChange(move(value, i, i + 1)) : undefined}
        >
          <I18nField
            label="Ad"
            value={w.name}
            onChange={(v) => onChange(replaceAt(value, i, { ...w, name: v }))}
          />
          <div className="grid2">
            <Field
              label="Partner / marka"
              value={w.partner}
              onChange={(v) => onChange(replaceAt(value, i, { ...w, partner: v }))}
            />
            <Field
              label="Saat"
              value={w.time}
              mono
              placeholder="12:00 – 14:00"
              onChange={(v) => onChange(replaceAt(value, i, { ...w, time: v }))}
            />
          </div>
          <I18nField
            label="Etiket"
            value={w.tag}
            onChange={(v) => onChange(replaceAt(value, i, { ...w, tag: v }))}
          />
          <I18nField
            label="Konum"
            value={w.loc}
            onChange={(v) => onChange(replaceAt(value, i, { ...w, loc: v }))}
          />
          <I18nField
            label="Kısa açıklama (kartta görünür)"
            value={w.desc}
            long
            rows={3}
            onChange={(v) => onChange(replaceAt(value, i, { ...w, desc: v }))}
          />
          <I18nField
            label="Uzun açıklama (detay açılınca)"
            value={w.detail}
            long
            rows={5}
            onChange={(v) => onChange(replaceAt(value, i, { ...w, detail: v }))}
          />
        </Panel>
      ))}
    </div>
  );
}

/* -------------------------------------------------------- sahne alanlar */

export function SpeakersEditor({
  value,
  onChange,
}: {
  value: Speaker[];
  onChange: (v: Speaker[]) => void;
}) {
  const add = () =>
    onChange([
      ...value,
      {
        id: newId("sp"),
        name: "",
        role: emptyI18n(),
        time: "",
        bio: emptyI18n(),
        quote: emptyI18n(),
        photo: "",
      },
    ]);

  return (
    <div>
      <Header
        title="Sahne alanlar"
        count={value.length + " kişi"}
        onAdd={add}
        addLabel="+ Kişi"
      />
      <p className="dim" style={{ fontSize: 14.5, marginTop: 8 }}>
        Sıralama sahneye çıkış sırasıdır — okları kullanın. Fotoğraf dosyaları
        <code style={{ margin: "0 4px" }}>public/fotograflar/</code> altında durur; buraya yalnızca
        dosya adını yazın (örn. <code>paul-craven.webp</code>).
      </p>

      {value.map((sp, i) => (
        <Panel
          key={sp.id}
          title={sp.name || "Yeni kişi"}
          onRemove={() => onChange(removeAt(value, i))}
          onUp={i > 0 ? () => onChange(move(value, i, i - 1)) : undefined}
          onDown={i < value.length - 1 ? () => onChange(move(value, i, i + 1)) : undefined}
        >
          <div className="grid2">
            <Field
              label="Ad soyad"
              value={sp.name}
              onChange={(v) => onChange(replaceAt(value, i, { ...sp, name: v }))}
            />
            <Field
              label="Saat"
              value={sp.time}
              mono
              placeholder="10:20 – 11:05"
              onChange={(v) => onChange(replaceAt(value, i, { ...sp, time: v }))}
            />
          </div>
          <Field
            label="Fotoğraf dosyası"
            value={sp.photo}
            mono
            placeholder="ad-soyad.webp"
            onChange={(v) => onChange(replaceAt(value, i, { ...sp, photo: v }))}
          />
          <I18nField
            label="Unvan / rol"
            value={sp.role}
            onChange={(v) => onChange(replaceAt(value, i, { ...sp, role: v }))}
          />
          <I18nField
            label="Not (2–4 cümle)"
            value={sp.bio}
            long
            rows={5}
            onChange={(v) => onChange(replaceAt(value, i, { ...sp, bio: v }))}
          />
          <I18nField
            label="Alıntı (opsiyonel)"
            value={sp.quote}
            long
            rows={2}
            onChange={(v) => onChange(replaceAt(value, i, { ...sp, quote: v }))}
          />
        </Panel>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------- dress code */

export function DressCodeEditor({
  value,
  onChange,
}: {
  value: DressCode;
  onChange: (v: DressCode) => void;
}) {
  return (
    <div>
      <Header title="Dress code" count="" />
      <p className="dim" style={{ fontSize: 14.5, marginTop: 8 }}>
        Banner görseli <code>public/gorseller/</code> altında durur; buraya dosya adını yazın.
        Boş bırakılırsa yer tutucu görünür.
      </p>
      <I18nField label="Başlık" value={value.title} onChange={(v) => onChange({ ...value, title: v })} />
      <Field
        label="Görsel dosyası"
        value={value.image}
        mono
        placeholder="dress-code.webp"
        onChange={(v) => onChange({ ...value, image: v })}
      />
      <I18nField
        label="Görsel alternatif metni"
        value={value.imageAlt}
        onChange={(v) => onChange({ ...value, imageAlt: v })}
      />
      <I18nField
        label="Açıklama"
        value={value.text}
        long
        rows={6}
        onChange={(v) => onChange({ ...value, text: v })}
      />
    </div>
  );
}

/* --------------------------------------------------------------- akşam */

export function EveningEditor({
  value,
  onChange,
}: {
  value: Evening;
  onChange: (v: Evening) => void;
}) {
  const add = () =>
    onChange({
      ...value,
      blocks: [...value.blocks, { id: newId("e"), time: "", title: emptyI18n(), text: emptyI18n() }],
    });

  return (
    <div>
      <Header title="Akşam" count={value.blocks.length + " bölüm"} onAdd={add} addLabel="+ Bölüm" />
      <I18nField
        label="Giriş metni"
        value={value.intro}
        long
        rows={3}
        onChange={(v) => onChange({ ...value, intro: v })}
      />

      {value.blocks.map((b, i) => (
        <Panel
          key={b.id}
          title={b.time + " · " + (b.title.tr || b.title.en || "Yeni bölüm")}
          onRemove={() => onChange({ ...value, blocks: removeAt(value.blocks, i) })}
          onUp={i > 0 ? () => onChange({ ...value, blocks: move(value.blocks, i, i - 1) }) : undefined}
          onDown={
            i < value.blocks.length - 1
              ? () => onChange({ ...value, blocks: move(value.blocks, i, i + 1) })
              : undefined
          }
        >
          <Field
            label="Saat"
            value={b.time}
            mono
            placeholder="19:30 – 20:00"
            onChange={(v) => onChange({ ...value, blocks: replaceAt(value.blocks, i, { ...b, time: v }) })}
          />
          <I18nField
            label="Başlık"
            value={b.title}
            onChange={(v) => onChange({ ...value, blocks: replaceAt(value.blocks, i, { ...b, title: v }) })}
          />
          <I18nField
            label="Metin"
            value={b.text}
            long
            rows={3}
            onChange={(v) => onChange({ ...value, blocks: replaceAt(value.blocks, i, { ...b, text: v }) })}
          />
        </Panel>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- etkinlik */

export function EventEditor({
  value,
  onChange,
}: {
  value: EventMeta;
  onChange: (v: EventMeta) => void;
}) {
  const add = () =>
    onChange({ ...value, practical: [...value.practical, { k: emptyI18n(), v: emptyI18n() }] });

  return (
    <div>
      <Header title="Etkinlik" count="" onAdd={add} addLabel="+ Pratik bilgi" />
      <Field label="Ad" value={value.name} onChange={(v) => onChange({ ...value, name: v })} />
      <I18nField
        label="Slogan"
        value={value.tagline}
        onChange={(v) => onChange({ ...value, tagline: v })}
      />
      <I18nField
        label="Tarih"
        value={value.dateLabel}
        onChange={(v) => onChange({ ...value, dateLabel: v })}
      />
      <I18nField label="Mekân" value={value.venue} onChange={(v) => onChange({ ...value, venue: v })} />

      {value.practical.map((row, i) => (
        <Panel
          key={i}
          title={row.k.tr || row.k.en || "Pratik bilgi"}
          onRemove={() => onChange({ ...value, practical: removeAt(value.practical, i) })}
          onUp={i > 0 ? () => onChange({ ...value, practical: move(value.practical, i, i - 1) }) : undefined}
          onDown={
            i < value.practical.length - 1
              ? () => onChange({ ...value, practical: move(value.practical, i, i + 1) })
              : undefined
          }
        >
          <I18nField
            label="Başlık"
            value={row.k}
            onChange={(k) => onChange({ ...value, practical: replaceAt(value.practical, i, { ...row, k }) })}
          />
          <I18nField
            label="Değer"
            value={row.v}
            long
            rows={2}
            onChange={(v) => onChange({ ...value, practical: replaceAt(value.practical, i, { ...row, v }) })}
          />
        </Panel>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- ortak */

function Header({
  title,
  count,
  onAdd,
  addLabel,
}: {
  title: string;
  count: string;
  onAdd?: () => void;
  addLabel?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div>
        <h2 className="serif" style={{ fontSize: 30, margin: 0 }}>
          {title}
        </h2>
        {count && <p className="dim" style={{ margin: "4px 0 0", fontSize: 14.5 }}>{count}</p>}
      </div>
      {onAdd && (
        <button type="button" className="btn btn--ghost" onClick={onAdd}>
          {addLabel}
        </button>
      )}
    </div>
  );
}

export type SectionKey = keyof Pick<
  SiteContent,
  "sessions" | "workshops" | "speakers" | "dressCode" | "evening" | "event"
>;
